import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DateField from "@/components/DateField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, AlertTriangle, ArrowRight, Trash2, CheckCircle2, History, Pencil } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatCurrency, localToday } from "@/lib/format";

const WEEKDAYS = ["Ma", "Di", "Wo", "Do", "Vr"];
const STATUS_TONE = {
  actief: "bg-emerald-50 text-emerald-700 border-emerald-200",
  concept: "bg-neutral-100 text-neutral-700 border-neutral-200",
  opgezegd: "bg-amber-50 text-amber-700 border-amber-200",
  beeindigd: "bg-red-50 text-red-700 border-red-200",
};

export default function ContractenPage() {
  const { user } = useAuth();
  const canWrite = can(user, "contracts", "write");
  const canCorrect = can(user, "contracts", "correct");
  const canDelete = can(user, "contracts", "delete");
  const [rows, setRows] = useState([]);
  const [kinderen, setKinderen] = useState([]);
  const [ouders, setOuders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(init());
  const [correctFor, setCorrectFor] = useState(null); // contract being corrected
  const [editFor, setEditFor] = useState(null); // contract being edited
  const [versiesFor, setVersiesFor] = useState(null); // contract being viewed for versions
  // Sync Alert — polling van mismatch endpoint zodat de directeur direct ziet
  // dat er contracten uit sync zijn gelopen met de kind-locatie.
  const [mismatchCount, setMismatchCount] = useState(null);

  const load = useCallback(async () => {
    const [c, k, o, l, g] = await Promise.all([
      api.get("/contracts"), api.get("/children"), api.get("/parents"),
      api.get("/locations"), api.get("/groups"),
    ]);
    setRows(c.data); setKinderen(k.data); setOuders(o.data); setLocations(l.data); setGroups(g.data);
    // Mismatch check draait apart: directeur/superadmin heeft er toegang toe,
    // andere rollen krijgen 403 → dan tonen we gewoon geen badge.
    try {
      const mm = await api.get("/admin/cleanup/contract-location-mismatch");
      setMismatchCount(mm.data?.total ?? 0);
    } catch { setMismatchCount(null); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = {
        ...form,
        uurtarief: parseFloat(form.uurtarief),
        uren_per_week: parseFloat(form.uren_per_week),
        contract_weeks_per_year: parseInt(form.contract_weeks_per_year || 52, 10),
        opvangdagen_per_week_override: form.opvangdagen_per_week_override === "" || form.opvangdagen_per_week_override == null
          ? null : parseFloat(form.opvangdagen_per_week_override),
        uren_per_dag_override: form.uren_per_dag_override === "" || form.uren_per_dag_override == null
          ? null : parseFloat(form.uren_per_dag_override),
        deelmaand_methode: form.deelmaand_methode || "gecontracteerde_dagen",
        vaste_toeslagen: form.vaste_toeslagen || [],
        vaste_kortingen: form.vaste_kortingen || [],
      };
      await api.post("/contracts", payload);
      toast.success("Contract aangemaakt");
      setOpen(false); setForm(init()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const kindMap = Object.fromEntries(kinderen.map(k => [k.id, k]));
  const ouderMap = Object.fromEntries(ouders.map(o => [o.id, o]));
  const locMap = Object.fromEntries(locations.map(l => [l.id, l.name]));

  const columns = [
    { key: "kind_id", label: "Kind", render: r => {
      const k = kindMap[r.kind_id];
      return k ? <div className="font-medium">{k.voornaam} {k.achternaam}</div> : <span className="text-neutral-400">—</span>;
    }},
    { key: "ouder_id", label: "Ouder", render: r => {
      const o = ouderMap[r.ouder_id];
      return o ? <div>{o.voornaam} {o.achternaam}</div> : <span className="text-neutral-400">—</span>;
    }},
    { key: "location_id", label: "Locatie", accessor: r => locMap[r.location_id] },
    { key: "start_date", label: "Start", render: r => new Date(r.start_date).toLocaleDateString('nl-NL') },
    { key: "dagen", label: "Dagen", sortable: false, render: r => (
      <div className="flex gap-1">
        {WEEKDAYS.map((w, i) => {
          const has = r.dagen?.some(d => d.weekday === i);
          return <span key={i} className={`w-6 h-6 text-[10px] rounded flex items-center justify-center border ${has ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "text-neutral-400 border-neutral-200"}`}>{w}</span>;
        })}
      </div>
    )},
    { key: "uren_per_week", label: "Uren/wk", render: r => <span className="font-mono">{r.uren_per_week}</span> },
    { key: "uurtarief", label: "Tarief", render: r => <span className="font-mono">{formatCurrency(r.uurtarief)}</span> },
    { key: "status", label: "Status", render: r => (
      <Badge variant="outline" className={`border ${STATUS_TONE[r.status] || ""}`}>{r.status}</Badge>
    )},
    { key: "acties", label: "", sortable: false, render: r => (
      <div className="flex items-center gap-1">
        {canWrite && r.status === "concept" && (
          <Button size="sm" variant="ghost" data-testid={`finalize-contract-${r.id}`}
            onClick={async (e) => {
              e.stopPropagation();
              const kindName = kindMap[r.kind_id] ? `${kindMap[r.kind_id].voornaam} ${kindMap[r.kind_id].achternaam}` : "dit kind";
              if (!window.confirm(`Contract definitief maken voor ${kindName}?\n\n• Status wordt gezet op 'definitief geaccepteerd'\n• Ouder(s) krijgen een activatie-uitnodiging per e-mail (72u geldig)\n• Bestaande ouder-accounts krijgen alleen het kind gekoppeld\n\nDoorgaan?`)) return;
              try {
                const { data } = await api.post(`/contracts/${r.id}/finalize`);
                const acties = data.invites?.actions || [];
                const nieuw = acties.filter(a => a.created_user).length;
                const gekoppeld = acties.filter(a => a.linked_to_existing_user).length;
                toast.success(`Contract definitief · ${nieuw} nieuwe uitnodiging(en) · ${gekoppeld} bestaand account gekoppeld`);
                load();
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Definitief maken mislukt");
              }
            }}
            className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50">
            <CheckCircle2 className="h-3 w-3 mr-1" /> Definitief maken
          </Button>
        )}
        {canCorrect && (
          <Button size="sm" variant="ghost" data-testid={`versies-contract-${r.id}`}
            onClick={(e) => { e.stopPropagation(); setVersiesFor(r); }}
            className="text-neutral-600 hover:text-neutral-900">
            <History className="h-3 w-3 mr-1" /> Versies
          </Button>
        )}
        {canWrite && (
          <Button size="sm" variant="ghost" data-testid={`edit-contract-${r.id}`}
            onClick={(e) => { e.stopPropagation(); setEditFor(r); }}
            className="text-[color:var(--sage)] hover:text-[color:var(--sage-hover)]">
            <Pencil className="h-3 w-3 mr-1" /> Aanpassen
          </Button>
        )}
        {canCorrect && (
          <Button size="sm" variant="ghost" data-testid={`correct-contract-${r.id}`}
            onClick={(e) => { e.stopPropagation(); setCorrectFor(r); }}
            className="text-[color:var(--terracotta)] hover:text-[color:var(--terracotta-hover)]">
            <AlertTriangle className="h-3 w-3 mr-1" /> Corrigeren
          </Button>
        )}
        {canDelete && (
          <Button size="sm" variant="ghost" data-testid={`delete-contract-${r.id}`}
            onClick={async (e) => {
              e.stopPropagation();
              const reason = window.prompt("Reden voor annuleren (audit-log):", "");
              if (reason === null) return;
              if (!window.confirm(`Contract annuleren voor ${kindMap[r.kind_id]?.voornaam || "—"}? Status wordt op 'geannuleerd' gezet. Het contract blijft zichtbaar in audit-log en historie.`)) return;
              try {
                await api.delete(`/contracts/${r.id}`, { params: { reason: reason || "" } });
                toast.success("Contract geannuleerd");
                load();
              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-3 w-3 mr-1" /> Annuleer
          </Button>
        )}
      </div>
    )},
  ];

  return (
    <div>
      {mismatchCount > 0 && (
        <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50 flex items-center justify-between gap-3 flex-wrap" data-testid="contract-sync-alert">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold">{mismatchCount}</span>
            <span>
              <strong>{mismatchCount}</strong> contract(en) staan op een andere locatie of groep dan het gekoppelde kind — facturatie kan mismatchen.
            </span>
          </div>
          <a href="/app/instellingen" className="inline-flex">
            <Button size="sm" variant="outline" className="border-amber-500 text-amber-900 hover:bg-amber-100" data-testid="sync-alert-open">
              Nu synchroniseren
            </Button>
          </a>
        </div>
      )}
      <PageHeader
        title="Contracten"
        subtitle="Actieve contracten per kind — dagdelen, uren en tarieven."
        testid="contracten-header"
        actions={canWrite && (
          <div className="flex items-center gap-2 flex-wrap">
            {user?.role === "superadmin" && <ContractsBackfillButton onDone={load} />}
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-contract-btn" style={{ background: "var(--sage)" }} className="text-white">
                <Plus className="h-4 w-4 mr-1" /> Nieuw contract
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader>
                <DialogTitle>Nieuw contract</DialogTitle>
                <DialogDescription>Koppel een kind aan een ouder, locatie en dagdelen.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Kind</Label>
                  <Select value={form.kind_id} onValueChange={v => {
                    const k = kindMap[v];
                    setForm({ ...form, kind_id: v, ouder_id: k?.parent_ids?.[0] || form.ouder_id, location_id: k?.location_id || form.location_id, group_id: k?.group_id || form.group_id });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Kies kind" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {kinderen.map(k => <SelectItem key={k.id} value={k.id}>{k.voornaam} {k.achternaam}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Ouder</Label>
                  <Select value={form.ouder_id} onValueChange={v => setForm({ ...form, ouder_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Kies ouder" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {ouders.map(o => <SelectItem key={o.id} value={o.id}>{o.voornaam} {o.achternaam}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Locatie</Label>
                  <Select value={form.location_id} onValueChange={v => setForm({ ...form, location_id: v, group_id: "" })}>
                    <SelectTrigger><SelectValue placeholder="Locatie" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Groep</Label>
                  <Select value={form.group_id} onValueChange={v => setForm({ ...form, group_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Groep" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {groups.filter(g => g.location_id === form.location_id).map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Startdatum</Label><DateField value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} data-testid="contract-start-date" /></div>
                <div><Label>Einddatum</Label><DateField value={form.end_date || ""} onChange={(v) => setForm({ ...form, end_date: v || null })} min={form.start_date} data-testid="contract-end-date" /></div>

                <div className="col-span-2">
                  <Label>Dagen (dagdelen)</Label>
                  <div className="grid grid-cols-5 gap-2 mt-1">
                    {WEEKDAYS.map((w, i) => {
                      const active = form.dagen.some(d => d.weekday === i);
                      return (
                        <button key={i} type="button"
                          onClick={() => {
                            const has = active;
                            setForm({ ...form, dagen: has ? form.dagen.filter(d => d.weekday !== i) : [...form.dagen, { weekday: i, dagdeel: "hele_dag" }] });
                          }}
                          className={`h-10 rounded-md text-sm border transition-colors ${active ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "border-neutral-200 hover:bg-neutral-100"}`}
                        >{w}</button>
                      );
                    })}
                  </div>
                </div>
                <div><Label>Uren/week</Label><Input type="number" step="0.5" value={form.uren_per_week} onChange={e => setForm({ ...form, uren_per_week: e.target.value })} data-testid="contract-uren-per-week" /></div>
                <div><Label>Uurtarief (€)</Label><Input type="number" step="0.01" value={form.uurtarief} onChange={e => setForm({ ...form, uurtarief: e.target.value })} data-testid="contract-uurtarief" /></div>

                {/* --- Facturatie-formule (Dienst Toeslagen) --- */}
                <div className="col-span-2 border-t pt-3 mt-1">
                  <div className="text-xs font-medium uppercase tracking-widest text-neutral-500 mb-2">
                    Facturatie-formule
                  </div>
                </div>
                <div>
                  <Label>Contractweken per jaar</Label>
                  <Select value={String(form.contract_weeks_per_year)}
                          onValueChange={v => setForm({ ...form, contract_weeks_per_year: parseInt(v, 10) })}>
                    <SelectTrigger data-testid="contract-weken-per-jaar"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="52">52 weken (heel jaar)</SelectItem>
                      <SelectItem value="51">51 weken</SelectItem>
                      <SelectItem value="48">48 weken (1 mnd vakantie)</SelectItem>
                      <SelectItem value="46">46 weken</SelectItem>
                      <SelectItem value="40">40 weken (BSO schoolweken)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Deelmaand-methode</Label>
                  <Select value={form.deelmaand_methode}
                          onValueChange={v => setForm({ ...form, deelmaand_methode: v })}>
                    <SelectTrigger data-testid="contract-deelmaand-methode"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="gecontracteerde_dagen">Gecontracteerde dagen</SelectItem>
                      <SelectItem value="naar_rato">Naar rato (kalenderdagen)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    Opvangdagen/week (override)
                    <span className="text-[10px] text-neutral-400">optioneel</span>
                  </Label>
                  <Input type="number" step="0.5" placeholder={`auto: ${form.dagen.length}`}
                         value={form.opvangdagen_per_week_override}
                         onChange={e => setForm({ ...form, opvangdagen_per_week_override: e.target.value })}
                         data-testid="contract-dagen-override" />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    Uren/dag (override)
                    <span className="text-[10px] text-neutral-400">optioneel</span>
                  </Label>
                  <Input type="number" step="0.25" placeholder={form.dagen.length && form.uren_per_week ? `auto: ${(parseFloat(form.uren_per_week) / form.dagen.length).toFixed(2)}` : "auto"}
                         value={form.uren_per_dag_override}
                         onChange={e => setForm({ ...form, uren_per_dag_override: e.target.value })}
                         data-testid="contract-uren-per-dag-override" />
                </div>

                {/* Live basisbedrag-preview */}
                <div className="col-span-2 bg-sage-50 rounded-md p-3 text-xs" style={{ background: "#f5f7f4" }} data-testid="contract-basis-preview">
                  {(() => {
                    const dpw = form.opvangdagen_per_week_override !== "" && form.opvangdagen_per_week_override != null
                      ? parseFloat(form.opvangdagen_per_week_override)
                      : form.dagen.length;
                    const upw = parseFloat(form.uren_per_week) || 0;
                    const upd = form.uren_per_dag_override !== "" && form.uren_per_dag_override != null
                      ? parseFloat(form.uren_per_dag_override)
                      : (dpw > 0 ? upw / dpw : 0);
                    const wpj = parseInt(form.contract_weeks_per_year || 52, 10);
                    const tarief = parseFloat(form.uurtarief) || 0;
                    const basis = dpw * upd * wpj / 12 * tarief;
                    return (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="text-neutral-600">
                          <strong>{dpw || 0}</strong> dg/wk × <strong>{upd ? upd.toFixed(2) : "0.00"}</strong> u/dg × <strong>{wpj}</strong> wkn ÷ 12 × <strong>€ {tarief.toFixed(2)}</strong>
                        </span>
                        <span className="ml-auto font-semibold text-sage-700">
                          = € {basis.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} basis/maand
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white">Opslaan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      />
      <DataTable columns={columns} rows={rows} testid="contracten-table" />
      {correctFor && <CorrectDialog contract={correctFor} onClose={() => setCorrectFor(null)} onDone={() => { setCorrectFor(null); load(); }} />}
      {editFor && <EditContractDialog contract={editFor} kinderen={kinderen} ouders={ouders} locations={locations} groups={groups}
                                       onClose={() => setEditFor(null)}
                                       onDone={() => { setEditFor(null); load(); }} />}
      {versiesFor && <VersiesDialog contract={versiesFor} onClose={() => setVersiesFor(null)} onDone={() => { setVersiesFor(null); load(); }} />}
    </div>
  );
}

function init() {
  return { kind_id: "", ouder_id: "", location_id: "", group_id: "",
    start_date: localToday(), end_date: null,
    dagen: [], uurtarief: 9.65, uren_per_week: 0, status: "actief",
    contract_weeks_per_year: 52,
    opvangdagen_per_week_override: "",
    uren_per_dag_override: "",
    deelmaand_methode: "gecontracteerde_dagen",
    vaste_toeslagen: [],
    vaste_kortingen: [],
  };
}

function CorrectDialog({ contract, onClose, onDone }) {
  const [impact, setImpact] = useState(null);
  const [changes, setChanges] = useState({
    uren_per_week: contract.uren_per_week,
    uurtarief: contract.uurtarief,
    end_date: contract.end_date || "",
    status: contract.status,
  });
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.post(`/contracts/${contract.id}/impact`).then(r => setImpact(r.data));
  }, [contract.id]);

  const save = async () => {
    if (!reason.trim()) { toast.error("Reden verplicht"); return; }
    setLoading(true);
    try {
      const payload = { reason, changes: { ...changes, uren_per_week: parseFloat(changes.uren_per_week), uurtarief: parseFloat(changes.uurtarief), end_date: changes.end_date || null } };
      await api.post(`/contracts/${contract.id}/correct`, payload);
      toast.success("Contract gecorrigeerd — audit trail bijgewerkt");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl" data-testid="correct-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[color:var(--terracotta)]" /> Contract corrigeren</DialogTitle>
          <DialogDescription>Retroactieve wijziging — impact wordt vooraf getoond. Originele waarden blijven bewaard in de audit trail.</DialogDescription>
        </DialogHeader>

        {impact && (
          <div className="mb-4 p-4 rounded-md border border-[color:var(--terracotta)]/30 bg-[color:var(--terracotta)]/5" data-testid="impact-preview">
            <div className="text-xs uppercase tracking-widest text-[color:var(--terracotta)] font-semibold mb-3">Impact-preview</div>
            <div className="grid grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-neutral-500">Facturen totaal</div>
                <div className="font-display text-xl">{impact.aantal_facturen}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Betaald</div>
                <div className="font-display text-lg text-emerald-700">{impact.betaald.aantal} · {formatCurrency(impact.betaald.totaal)}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Verzonden</div>
                <div className="font-display text-lg text-blue-700">{impact.verzonden.aantal} · {formatCurrency(impact.verzonden.totaal)}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Concept</div>
                <div className="font-display text-lg text-neutral-700">{impact.concept.aantal} · {formatCurrency(impact.concept.totaal)}</div>
              </div>
            </div>
            {impact.waarschuwingen.length > 0 && (
              <div className="mt-3 space-y-1">
                {impact.waarschuwingen.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-[color:var(--terracotta)]">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {w}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Uren/week</Label>
            <div className="flex items-center gap-2">
              <div className="text-neutral-400 line-through font-mono">{contract.uren_per_week}</div>
              <ArrowRight className="h-3 w-3 text-neutral-400" />
              <Input type="number" step="0.5" value={changes.uren_per_week} onChange={e => setChanges({ ...changes, uren_per_week: e.target.value })} data-testid="correct-uren" />
            </div>
          </div>
          <div>
            <Label>Uurtarief</Label>
            <div className="flex items-center gap-2">
              <div className="text-neutral-400 line-through font-mono">{formatCurrency(contract.uurtarief)}</div>
              <ArrowRight className="h-3 w-3 text-neutral-400" />
              <Input type="number" step="0.01" value={changes.uurtarief} onChange={e => setChanges({ ...changes, uurtarief: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Einddatum</Label>
            <DateField value={changes.end_date} onChange={(v) => setChanges({ ...changes, end_date: v })} data-testid="contract-verlengen-end-date" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={changes.status} onValueChange={v => setChanges({ ...changes, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {["actief", "opgezegd", "beeindigd", "concept"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-[color:var(--terracotta)] font-semibold">Reden voor correctie (verplicht)</Label>
          <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Bijv. Retroactieve wijziging o.b.v. ondertekend contractamendement d.d. …"
            data-testid="correct-reason" />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={save} disabled={loading || !reason.trim()} data-testid="correct-save"
            style={{ background: "var(--terracotta)" }} className="text-white">
            {loading ? "Bezig…" : "Correctie doorvoeren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Bulk-genereer default contracten voor alle kinderen zonder actief contract.
 * Handig direct na een data-import zodat presentie-backfill contract_days modus werkt. */
function ContractsBackfillButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    start_date: localToday(),
    end_date: "",
    weekdays: [0, 1, 2, 3, 4],
    dagdeel: "hele_dag",
    uurtarief: 9.65,
    only_missing: true,
  });
  const [result, setResult] = useState(null);
  const WEEK_LABELS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

  const toggleDay = (i) => {
    setForm(f => ({
      ...f,
      weekdays: f.weekdays.includes(i) ? f.weekdays.filter(x => x !== i) : [...f.weekdays, i].sort(),
    }));
  };

  const run = async () => {
    if (form.weekdays.length === 0) { toast.error("Kies minstens 1 weekdag"); return; }
    setBusy(true); setResult(null);
    try {
      const payload = { ...form, end_date: form.end_date || null };
      const { data } = await api.post("/contracts/backfill", payload);
      setResult(data);
      toast.success(`${data.created} contracten aangemaakt · ${data.skipped} overgeslagen`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Backfill mislukt");
      setResult({ error: true });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="contracts-backfill-btn">
        <Plus className="h-4 w-4 mr-1" /> Backfill contracten
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="contracts-backfill-dialog">
          <DialogHeader>
            <DialogTitle>Contracten genereren</DialogTitle>
            <DialogDescription>
              Maak een default-contract voor elk kind zonder actief contract, met dezelfde
              weekdagen en dagdeel. Per kind kun je dit daarna fine-tunen.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Startdatum</Label>
              <DateField value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} data-testid="cb-start" />
            </div>
            <div><Label>Einddatum <span className="text-neutral-400 text-[10px]">(optioneel)</span></Label>
              <DateField value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} min={form.start_date} data-testid="cb-end" />
            </div>
            <div className="col-span-2">
              <Label>Weekdagen</Label>
              <div className="flex gap-1.5 mt-1">
                {WEEK_LABELS.map((lbl, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                      form.weekdays.includes(i)
                        ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]"
                        : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"
                    }`}
                    data-testid={`cb-day-${i}`}
                  >{lbl}</button>
                ))}
              </div>
            </div>
            <div><Label>Dagdeel</Label>
              <Select value={form.dagdeel} onValueChange={v => setForm({ ...form, dagdeel: v })}>
                <SelectTrigger data-testid="cb-dagdeel"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="hele_dag">Hele dag (10u)</SelectItem>
                  <SelectItem value="ochtend">Ochtend (5u)</SelectItem>
                  <SelectItem value="middag">Middag (5u)</SelectItem>
                  <SelectItem value="na_school">Na school BSO (3.5u)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Uurtarief (€)</Label>
              <Input type="number" step="0.01" value={form.uurtarief} onChange={e => setForm({ ...form, uurtarief: parseFloat(e.target.value) })} data-testid="cb-uurtarief" />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-xs text-neutral-700">
              <input type="checkbox" checked={form.only_missing} onChange={e => setForm({ ...form, only_missing: e.target.checked })} data-testid="cb-only-missing" />
              Alleen kinderen zonder actief contract (aanbevolen)
            </label>
          </div>
          {result && !result.error && (
            <div className="mt-3 p-3 rounded-md border border-emerald-200 bg-emerald-50 text-xs text-emerald-900" data-testid="cb-result">
              ✓ {result.created} contracten aangemaakt · {result.skipped} overgeslagen ·
              {" "}{result.uren_per_week} uur/week per contract
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Sluiten</Button>
            <Button onClick={run} disabled={busy} data-testid="cb-run" style={{ background: "var(--sage)" }} className="text-white">
              {busy ? "Bezig…" : "Genereer contracten"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function VersiesDialog({ contract, onClose, onDone }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nv, setNv] = useState({
    effective_from: new Date().toISOString().slice(0, 10),
    uurtarief: contract.uurtarief || 9.65,
    uren_per_week: contract.uren_per_week || 0,
    end_date: contract.end_date || "",
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/${contract.id}/versions`);
      setVersions(data.versions || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contract.id]);

  const submit = async () => {
    if (!nv.effective_from) { toast.error("Ingangsdatum verplicht"); return; }
    setBusy(true);
    try {
      const payload = {
        effective_from: nv.effective_from,
        uurtarief: parseFloat(nv.uurtarief),
        uren_per_week: parseFloat(nv.uren_per_week),
        end_date: nv.end_date || null,
        notes: nv.notes || null,
      };
      await api.post(`/contracts/${contract.id}/versions`, payload);
      toast.success("Nieuwe versie aangemaakt");
      setShowNew(false);
      await load();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Aanmaken mislukt"); }
    setBusy(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="versies-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Contract-versies</DialogTitle>
          <DialogDescription>
            Elke wijziging is een nieuwe versie met ingangsdatum. Facturen worden altijd
            gegenereerd uit de versie die geldig was op de 1e dag van de factuurperiode
            (regel 5 & 6).
          </DialogDescription>
        </DialogHeader>

        {loading ? <div className="text-center text-neutral-500 py-6">Laden…</div> : (
          <div className="space-y-2">
            {versions.map(v => (
              <div key={v.id} className="p-3 border rounded-md" data-testid={`version-${v.version_no}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-sm">Versie {v.version_no}</div>
                  <div className="text-xs">
                    {v.effective_from} {v.effective_to ? `→ ${v.effective_to}` : "→ heden"}
                  </div>
                </div>
                <div className="text-xs text-neutral-600 grid grid-cols-2 md:grid-cols-4 gap-1">
                  <span>€{v.snapshot?.uurtarief?.toFixed?.(2) || "—"}/uur</span>
                  <span>{v.snapshot?.uren_per_week || 0} uur/week</span>
                  <span>Dagen: {(v.snapshot?.dagen || []).length}</span>
                  <span>Groep: {v.snapshot?.group_id?.slice(0, 8) || "—"}</span>
                </div>
                {v.notes && <div className="text-xs italic text-neutral-500 mt-1">"{v.notes}"</div>}
              </div>
            ))}
          </div>
        )}

        {!showNew ? (
          <div className="pt-3">
            <Button onClick={() => setShowNew(true)} style={{ background: "var(--sage)" }} className="text-white" data-testid="btn-nieuwe-versie">
              <Plus className="h-4 w-4 mr-1" /> Nieuwe versie met ingangsdatum
            </Button>
          </div>
        ) : (
          <div className="p-4 border-2 border-[color:var(--sage)] rounded-md mt-3 space-y-3">
            <div className="font-medium text-sm">Nieuwe versie</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Ingangsdatum *</Label>
                <DateField value={nv.effective_from} onChange={(v) => setNv({ ...nv, effective_from: v })} data-testid="nv-effective-from" />
              </div>
              <div>
                <Label className="text-xs">Uurtarief (€)</Label>
                <Input type="number" step="0.01" value={nv.uurtarief} onChange={e => setNv({ ...nv, uurtarief: e.target.value })} data-testid="nv-uurtarief" />
              </div>
              <div>
                <Label className="text-xs">Uren per week</Label>
                <Input type="number" step="0.5" value={nv.uren_per_week} onChange={e => setNv({ ...nv, uren_per_week: e.target.value })} data-testid="nv-uren" />
              </div>
              <div>
                <Label className="text-xs">Einddatum (optioneel)</Label>
                <DateField value={nv.end_date || ""} onChange={(v) => setNv({ ...nv, end_date: v })} min={nv.effective_from} data-testid="nv-end-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notities</Label>
              <Textarea rows={2} value={nv.notes} onChange={e => setNv({ ...nv, notes: e.target.value })}
                placeholder="Bijv. Verhoging na jaarlijkse cao-indexatie" data-testid="nv-notes" />
            </div>
            <div className="text-xs text-neutral-500 bg-amber-50 border border-amber-200 rounded p-2">
              💡 Deze versie geldt vanaf <strong>{nv.effective_from}</strong>. De vorige versie wordt automatisch
              afgesloten op de dag ervoor. Al betaalde facturen blijven ongewijzigd.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowNew(false)}>Annuleren</Button>
              <Button onClick={submit} disabled={busy} style={{ background: "var(--sage)" }} className="text-white" data-testid="nv-save">
                {busy ? "Bezig…" : "Versie aanmaken"}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * WEBSTER KOV — EditContractDialog
 * Volledig bewerkbaar contract: kind, ouder, locatie, groep, tarieven, dagen,
 * start/eind, uren/week, status. Kritieke velden (id, organization_id) blijven
 * immutable server-side.
 */
const DAG_LABELS = ["Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag","Zondag"];
const DAGDEEL_OPTIONS = [
  {v: "hele_dag", l: "Hele dag"},
  {v: "ochtend",  l: "Ochtend"},
  {v: "middag",   l: "Middag"},
];

function EditContractDialog({ contract, kinderen, ouders, locations, groups, onClose, onDone }) {
  const [form, setForm] = useState({
    kind_id: contract.kind_id || "",
    ouder_id: contract.ouder_id || "",
    location_id: contract.location_id || "",
    group_id: contract.group_id || "",
    start_date: contract.start_date || "",
    end_date: contract.end_date || "",
    uurtarief: contract.uurtarief ?? 9.65,
    uren_per_week: contract.uren_per_week ?? 0,
    status: contract.status || "concept",
    dagen: contract.dagen || [],
  });
  const [busy, setBusy] = useState(false);

  const toggleDag = (weekday, dagdeel) => {
    const has = form.dagen.some(d => d.weekday === weekday && d.dagdeel === dagdeel);
    if (has) {
      setForm({ ...form, dagen: form.dagen.filter(d => !(d.weekday === weekday && d.dagdeel === dagdeel)) });
    } else {
      // Verwijder eventuele andere dagdelen voor deze weekday (1 keuze per dag)
      const cleaned = form.dagen.filter(d => d.weekday !== weekday);
      setForm({ ...form, dagen: [...cleaned, { weekday, dagdeel }] });
    }
  };

  const save = async () => {
    if (!form.kind_id || !form.ouder_id || !form.location_id) {
      toast.error("Kind, ouder en locatie zijn verplicht");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/contracts/${contract.id}`, {
        kind_id: form.kind_id,
        ouder_id: form.ouder_id,
        location_id: form.location_id,
        group_id: form.group_id || null,
        start_date: form.start_date,
        end_date: form.end_date || null,
        uurtarief: Number(form.uurtarief) || 0,
        uren_per_week: Number(form.uren_per_week) || 0,
        status: form.status,
        dagen: form.dagen,
      });
      toast.success("Contract bijgewerkt");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="edit-contract-dialog">
        <DialogHeader>
          <DialogTitle>Contract aanpassen</DialogTitle>
          <DialogDescription>
            Wijzig alle velden. Historische wijzigingen worden vastgelegd in het audit-log.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-2">
          <div>
            <Label>Kind</Label>
            <Select value={form.kind_id} onValueChange={v => setForm({ ...form, kind_id: v })}>
              <SelectTrigger data-testid="edit-kind"><SelectValue placeholder="Kies kind" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {kinderen.map(k => <SelectItem key={k.id} value={k.id}>{k.voornaam} {k.achternaam}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Ouder</Label>
            <Select value={form.ouder_id} onValueChange={v => setForm({ ...form, ouder_id: v })}>
              <SelectTrigger data-testid="edit-ouder"><SelectValue placeholder="Kies ouder" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {ouders.map(o => <SelectItem key={o.id} value={o.id}>{o.voornaam} {o.achternaam}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Locatie</Label>
            <Select value={form.location_id} onValueChange={v => setForm({ ...form, location_id: v, group_id: "" })}>
              <SelectTrigger data-testid="edit-locatie"><SelectValue placeholder="Kies locatie" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Groep</Label>
            <Select value={form.group_id || ""} onValueChange={v => setForm({ ...form, group_id: v })}>
              <SelectTrigger data-testid="edit-groep"><SelectValue placeholder="Kies groep" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {groups.filter(g => !form.location_id || g.location_id === form.location_id)
                       .map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Startdatum</Label>
            <DateField value={form.start_date} onChange={v => setForm({ ...form, start_date: v })} testid="edit-start"/>
          </div>
          <div>
            <Label>Einddatum <span className="text-neutral-400 text-xs">(leeg = onbepaald)</span></Label>
            <DateField value={form.end_date} onChange={v => setForm({ ...form, end_date: v })} testid="edit-eind"/>
          </div>
          <div>
            <Label>Uurtarief (€)</Label>
            <Input type="number" step="0.01" value={form.uurtarief}
                   onChange={e => setForm({ ...form, uurtarief: e.target.value })}
                   data-testid="edit-uurtarief"/>
            <KotWarning
              oldTarief={Number(contract.uurtarief) || 0}
              newTarief={Number(form.uurtarief) || 0}
              urenPerWeek={Number(form.uren_per_week) || 0}
              locationId={form.location_id}
              locations={locations}
            />
          </div>
          <div>
            <Label>Uren per week</Label>
            <Input type="number" step="0.5" value={form.uren_per_week}
                   onChange={e => setForm({ ...form, uren_per_week: e.target.value })}
                   data-testid="edit-uren"/>
          </div>
          <div className="col-span-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="concept">Concept</SelectItem>
                <SelectItem value="getekend">Getekend</SelectItem>
                <SelectItem value="actief">Actief</SelectItem>
                <SelectItem value="definitief_geaccepteerd">Definitief</SelectItem>
                <SelectItem value="beeindigd">Beëindigd</SelectItem>
                <SelectItem value="geannuleerd">Geannuleerd</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Dagen & dagdelen</Label>
            <div className="border rounded p-2 mt-1 space-y-1" data-testid="edit-dagen">
              {DAG_LABELS.slice(0, 5).map((label, wd) => (
                <div key={wd} className="flex items-center gap-2 text-xs">
                  <span className="w-20 text-neutral-700">{label}</span>
                  {DAGDEEL_OPTIONS.map(opt => {
                    const active = form.dagen.some(d => d.weekday === wd && d.dagdeel === opt.v);
                    return (
                      <button key={opt.v} type="button"
                              onClick={() => toggleDag(wd, opt.v)}
                              data-testid={`edit-dag-${wd}-${opt.v}`}
                              className={`px-2 py-0.5 rounded border ${active
                                ? "bg-sage-100 border-sage-400 text-sage-900"
                                : "bg-white border-neutral-200 text-neutral-500 hover:bg-neutral-50"}`}>
                        {opt.l}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={save} disabled={busy} style={{ background: "var(--sage)" }}
                  className="text-white" data-testid="edit-save">
            {busy ? "Opslaan…" : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * WEBSTER KOV — KOT-Warning
 *
 * Toont naast/onder het uurtarief-veld direct het effect op de KOT-toeslag van de
 * ouder. Rekent met de wettelijke max-uurprijzen (Belastingdienst 2026):
 *   KDV: €11,13/u  |  BSO: €10,32/u  |  GO: €8,71/u
 * Formule voor "boven KOT-max": (tarief − max) × uren_per_maand → ouder betaalt
 * dit deel volledig zelf (0% toeslag). Dit is de meest impactvolle waarschuwing;
 * de exacte KOT-toeslag zelf hangt af van inkomen en wordt daarom niet berekend.
 */
function KotWarning({ oldTarief, newTarief, urenPerWeek, locationId, locations }) {
  const location = (locations || []).find(l => l.id === locationId);
  const type = (location?.type || "KDV").toUpperCase();
  const MAX = { KDV: 11.13, BSO: 10.32, GO: 8.71 };
  const max = MAX[type] || MAX.KDV;

  if (!newTarief || newTarief === oldTarief) return null;

  const urenPerMaand = (Number(urenPerWeek) || 0) * (52 / 12);
  const oldOver = Math.max(0, oldTarief - max);
  const newOver = Math.max(0, newTarief - max);
  const oldEigenBijdrage = oldOver * urenPerMaand;
  const newEigenBijdrage = newOver * urenPerMaand;
  const deltaBedrag = newEigenBijdrage - oldEigenBijdrage;

  // Geen waarschuwing als beide binnen KOT-max blijven én tarief niet significant wijzigt
  if (newOver === 0 && oldOver === 0) {
    return (
      <div className="mt-1 text-[10px] text-emerald-700 flex items-center gap-1" data-testid="kot-ok">
        ✓ binnen KOT-max (€{max.toFixed(2)}/u · {type})
      </div>
    );
  }

  const richting = newEigenBijdrage > oldEigenBijdrage ? "up" : "down";
  const tone = richting === "up" ? "bg-red-50 border-red-200 text-red-900" : "bg-amber-50 border-amber-200 text-amber-900";
  return (
    <div className={`mt-1 text-[10px] px-2 py-1 rounded border ${tone}`} data-testid="kot-warning">
      <div className="font-semibold uppercase tracking-widest text-[9px] mb-0.5">
        ⚠ KOT-impact ({type} max €{max.toFixed(2)}/u)
      </div>
      <div>
        Tarief boven KOT-max: <span className="tabular-nums">€{newOver.toFixed(2)}/u</span>
        {" → eigen bijdrage ouder ≈ "}
        <span className="tabular-nums font-semibold">€{newEigenBijdrage.toFixed(0)}/mnd</span>
      </div>
      {deltaBedrag !== 0 && (
        <div className="opacity-80">
          Wijziging t.o.v. huidig: <span className="tabular-nums font-semibold">{deltaBedrag > 0 ? "+" : ""}€{deltaBedrag.toFixed(0)}/mnd</span> voor de ouder
        </div>
      )}
    </div>
  );
}
